import { convertDateTime, errorMessage, formatDateTime } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { useToasts } from "react-toast-notifications";
import reasonService from "services/reasonService";
import orderService from "services/orderService";
import { formConvertErrorMessage } from "../../helpers/form_helper";

export const useListReason = (payload, onSuccess, onError) => {
  const { params, queryKey } = payload;
  return useQuery(
    [queryKey || "reason-list", params?.page],
    () => reasonService.getReasons(payload),
    {
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      initialData: Object({ data: [] }),
      select: response => {
        const { data } = response;
        if (!data?.items) {
          return {
            items: [],
          };
        }
        return {
          items: data?.items || [],
          params: params,
          totalCount: data._meta ? data._meta.totalCount : data?.items?.length,
        };
      },
    }
  );
};

// Get Reason Types
export const useReasonTypes = (onSuccess, onError) => {
  const { t } = useTranslation(["reason"]);
  const { addToast } = useToasts();

  return useQuery(["reason-types"], () => reasonService.getReasonTypes(), {
    enabled: true,
    retry: 1,
    refetchOnWindowFocus: false,
    onSuccess,
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.fetch_reason_types", {
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
    initialData: Object({ data: [] }),
    select: response => {
      if (response?.data) {
        return Object.keys(response.data).map(key => ({
          value: key,
          label: response.data[key],
        }));
      }
      return [];
    },
  });
};

// Get Order Types
export const useOrderTypes = (onSuccess, onError) => {
  const { t } = useTranslation(["reason"]);
  const { addToast } = useToasts();

  return useQuery(["order-types"], () => orderService.listTypeOrder(), {
    enabled: true,
    retry: 1,
    refetchOnWindowFocus: false,
    onSuccess,
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.fetch_order_types", {
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
    initialData: Object({ data: [] }),
    select: response => {
      if (response) {
        return Object.keys(response).map(key => ({
          value: key,
          label: response[key],
        }));
      }
      return [];
    },
  });
};

// Detail reason
export const useDetailReason = (payload, onSuccess, onError) => {
  return useQuery(
    ["reason", "detail", payload],
    () => reasonService.detailReason(payload),
    {
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return data;
      },
    }
  );
};

// Create Reason
export const useCreateReason = (onSuccess, onError) => {
  const { t } = useTranslation(["reason"]);
  const { addToast } = useToasts();

  return useMutation(reasonService.createReason, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.create", {
          target: t("reason"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.create", {
              target: t("reason"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// Update Reason
export const useUpdateReason = (onSuccess, onError) => {
  const { t } = useTranslation(["reason"]);
  const { addToast } = useToasts();

  return useMutation(reasonService.updateReason, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.update", {
          target: t("reason"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.update", {
              target: t("reason"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// Delete Reason
export const useDeleteReason = (onSuccess, onError) => {
  const { t } = useTranslation(["reason"]);
  const { addToast } = useToasts();

  return useMutation(reasonService.deleteReason, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.delete", {
          target: t("reason"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.delete", {
              target: t("reason"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};
